import React, {
  FC,
  MutableRefObject,
  ReactNode,
  useCallback,
  useMemo,
  useRef,
  useState,
} from 'react';
import { JobPhaseReceivableSheetInterface } from '@vatos-pas/common';

import { InvoiceFieldKey } from '../utils/validateInvoice';
import { createRowRegistry } from './createRowRegistry';

export type InvoiceFieldErrors = Partial<Record<InvoiceFieldKey, string>>;

/**
 * Editable state for one row's invoice cells. `*Input` are the controlled
 * field values; `committed*` are the last-persisted baselines that drive
 * `hasChanges` and the Save/Update label.
 */
export type InvoiceDraft = {
  poAmountInput: string;
  invoiceNumberInput: string;
  committedPO: number | null;
  committedInvoice: string | null;
  errors: InvoiceFieldErrors;
  submitting: boolean;
};

type InvoiceDraftRegistryValue = {
  /** Reactive read of a row's draft (undefined until seeded); drives re-render. */
  getDraft: (jobPhaseId: string) => InvoiceDraft | undefined;
  /** Synchronous (non-reactive) read for write-path / effect decisions. */
  peekDraft: (jobPhaseId: string) => InvoiceDraft | undefined;
  setDraft: (jobPhaseId: string, partial: Partial<InvoiceDraft>) => void;
  /**
   * Overwrite a row's draft + baseline from its record, at most once per record
   * object (dedup by record reference). The Datagrid passes a single record
   * reference to all of a row's cells, so the sibling cell's call for the same
   * record is a no-op, and a list refetch (new record object) resyncs server
   * truth.
   */
  syncFromRecord: (
    jobPhaseId: string,
    record: JobPhaseReceivableSheetInterface,
    seed: InvoiceDraft,
  ) => void;
  /**
   * Mark a record object as already consumed by the sync without writing a
   * draft. Used when a refetch lands during the row's own save: the in-flight
   * guard skips the reseed, and marking the incoming record stops a later pass
   * from reseeding over the baseline the save is about to adopt.
   */
  markSynced: (
    jobPhaseId: string,
    record: JobPhaseReceivableSheetInterface,
  ) => void;
  /**
   * Shared per-row in-flight flag. Set around the row's own save so the
   * `syncFromRecord` effect can't clobber the optimistic edit while it commits.
   */
  pendingRef: MutableRefObject<Record<string, boolean>>;
};

const {
  Context: InvoiceDraftRegistryContext,
  useRegistry: useInvoiceDraftRegistry,
} = createRowRegistry<InvoiceDraftRegistryValue>(
  'useInvoiceDraftRegistry',
  'InvoiceDraftRegistryProvider',
);

export { useInvoiceDraftRegistry };

/**
 * Row-scoped invoice-draft store, provided once around the receivables list.
 *
 * The invoice column is split into separate sibling Datagrid cells (PO Amount /
 * Invoice # / Save / history icon). They live in separate `<td>`s but share one
 * draft and one Save button, so the edit state is lifted here and keyed by
 * jobPhaseId. Mirrors `ReceivableRegistry`, which similarly shares the row's
 * receivable id across its editable cells.
 *
 * `getDraft` is the reactive read (recreated when `drafts` changes) so editing
 * cells re-render as the user types. The write callbacks are identity-stable
 * (they touch only refs + the functional setState updater), so the seed/resync
 * effect in useInvoiceDraft can depend on them and run only when `record`
 * changes — not on every keystroke. `peekDraft` mirrors `ReceivableRegistry`'s
 * `peekId`: a synchronous ref read for that effect's clean/dirty decision.
 *
 * Re-render scope: editing a row changes the context value (via `getDraft`), so
 * the rendered invoice cells re-render per keystroke. Acceptable because the
 * Datagrid only mounts the current (small) page of rows.
 */
export const InvoiceDraftRegistryProvider: FC<{ children: ReactNode }> = ({
  children,
}) => {
  const [drafts, setDrafts] = useState<Record<string, InvoiceDraft>>({});
  const draftsRef = useRef<Record<string, InvoiceDraft>>({});
  const pendingRef = useRef<Record<string, boolean>>({});
  const lastSyncedRef = useRef<
    Record<string, JobPhaseReceivableSheetInterface>
  >({});

  const getDraft = useCallback(
    (jobPhaseId: string) => drafts[jobPhaseId],
    [drafts],
  );

  const peekDraft = useCallback(
    (jobPhaseId: string) => draftsRef.current[jobPhaseId],
    [],
  );

  const setDraft = useCallback(
    (jobPhaseId: string, partial: Partial<InvoiceDraft>) =>
      setDrafts(prev => {
        const next = {
          ...prev,
          [jobPhaseId]: { ...prev[jobPhaseId], ...partial },
        };
        draftsRef.current = next;
        return next;
      }),
    [],
  );

  const syncFromRecord = useCallback(
    (
      jobPhaseId: string,
      record: JobPhaseReceivableSheetInterface,
      seed: InvoiceDraft,
    ) => {
      if (lastSyncedRef.current[jobPhaseId] === record) return;
      lastSyncedRef.current[jobPhaseId] = record;
      setDrafts(prev => {
        const next = { ...prev, [jobPhaseId]: seed };
        draftsRef.current = next;
        return next;
      });
    },
    [],
  );

  const markSynced = useCallback(
    (jobPhaseId: string, record: JobPhaseReceivableSheetInterface) => {
      lastSyncedRef.current[jobPhaseId] = record;
    },
    [],
  );

  const value = useMemo<InvoiceDraftRegistryValue>(
    () => ({
      getDraft,
      peekDraft,
      setDraft,
      syncFromRecord,
      markSynced,
      pendingRef,
    }),
    [getDraft, peekDraft, setDraft, syncFromRecord, markSynced],
  );

  return (
    <InvoiceDraftRegistryContext.Provider value={value}>
      {children}
    </InvoiceDraftRegistryContext.Provider>
  );
};
